const SOLO_FECHA = /^\d{4}-\d{2}-\d{2}$/;

// "2026-09-14" lo parsea el navegador como medianoche UTC, y en Chile eso cae el 13.
// Las fechas sin hora tienen que construirse en horario local.
function toDate(dateString) {
  return SOLO_FECHA.test(dateString)
    ? parseLocalDate(dateString)
    : new Date(dateString);
}

export function formatDate(dateString, withTime = false, treatAsUTC = false) {
  if (
    !dateString ||
    dateString.startsWith('1900') ||
    dateString.startsWith('0001')
  ) {
    return '--';
  }

  let date;

  if (treatAsUTC) {
    const normalized = dateString.endsWith('Z') ? dateString : `${dateString}Z`;

    date = new Date(normalized);
  } else {
    date = toDate(dateString);
  }

  if (isNaN(date)) return '--';

  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();

  if (!withTime) {
    return `${day}/${month}/${year}`;
  }

  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');

  return `${day}/${month}/${year} ${hours}:${minutes}`;
}

export function removeTime(dateString) {
  if (!dateString) return '';

  if (dateString.includes('T')) {
    return dateString.split('T')[0];
  }

  if (dateString.includes(' ')) {
    return dateString.split(' ')[0];
  }

  return dateString;
}

export function parseLocalDate(dateString) {
  if (!dateString) return null;

  const [year, month, day] = dateString.split('-').map(Number);

  if (!year || !month || !day) return null;

  return new Date(year, month - 1, day);
}

/**
 * Formatea una fecha SIN hora (`yyyy-MM-dd`) como `dd/mm/yyyy`.
 *
 * Existe aparte de `formatDate` porque esa usa `new Date('2026-09-09')`, que se parsea como
 * medianoche UTC; con `getDate()` en horario de Chile eso devuelve el día anterior. Para un
 * `DateOnly` del backend no hay instante ni zona: es un día del calendario y hay que leerlo
 * literal. Se apoya en `parseLocalDate`, que lo arma componente por componente.
 *
 * @param {string|null|undefined} dateString fecha en formato `yyyy-MM-dd`
 * @param {string} [vacio='—'] qué devolver si no hay fecha
 */
export function formatDateOnly(dateString, vacio = '—') {
  if (!dateString) return vacio;

  const soloFecha = removeTime(String(dateString));
  const date = parseLocalDate(soloFecha);

  if (!date || Number.isNaN(date.getTime())) return vacio;

  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');

  return `${day}/${month}/${date.getFullYear()}`;
}

const MESES = [
  'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
];

/**
 * La fecha como se escribe en un escrito judicial: «9 de marzo de 2022». Misma regla que
 * `FechaJudicial.EnPalabras` en el backend.
 */
export function formatDateInWords(dateString, vacio = '—') {
  if (!dateString) return vacio;

  const date = parseLocalDate(removeTime(String(dateString)));
  if (!date || Number.isNaN(date.getTime())) return vacio;

  return `${date.getDate()} de ${MESES[date.getMonth()]} de ${date.getFullYear()}`;
}

export function formatDateByLocale(dateString, locale) {
  if (
    !dateString ||
    dateString.startsWith('1900') ||
    dateString.startsWith('0001')
  ) {
    return '--';
  }

  const date = toDate(dateString);
  if (!date || Number.isNaN(date.getTime())) return '--';

  return new Intl.DateTimeFormat(locale).format(date);
}
